import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Popover,
  Tag,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import {
  Dispatch,
  FC,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

const DATE_FORMAT = 'YYYY-MM-DD'

export type DescriptionValue = string | number | Date | undefined

interface EditableProps<T = string | number> {
  value: T | undefined
  onChange?: (value: T | undefined) => void
  referenceValues?: ReadonlyArray<T | undefined>
}

const FullWidthInput = styled(Input)`
  width: 100%;
`

const FullWidthTextArea = styled(Input.TextArea)`
  width: 100%;
`

const StyledInputNumber = styled(InputNumber<number>)`
  width: 200px;
`

const StyledDatePicker = styled(DatePicker<dayjs.Dayjs>)`
  width: 200px;
`

export const formatDescriptionValue = (value: DescriptionValue): string => {
  if (value === undefined || value === null) return '-'
  if (value instanceof Date) return value.toISOString().split('T')[0]
  return String(value)
}

const RawEditableString: FC<EditableProps<string>> = ({
  value,
  onChange,
  referenceValues,
}) => {
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value)
    },
    [onChange]
  )

  const handleReferenceSelect = useCallback(
    (val: string | number | undefined) => {
      onChange?.(val as string | undefined)
    },
    [onChange]
  )

  if (!onChange) {
    return <>{formatDescriptionValue(value)}</>
  }

  return (
    <FieldContainer>
      <FullWidthInput value={value} onChange={handleInputChange} />
      {referenceValues && referenceValues.length > 0 && (
        <ReferenceValuesTag
          referenceValues={referenceValues}
          onSelect={handleReferenceSelect}
        />
      )}
    </FieldContainer>
  )
}

export const EditableString = memo(RawEditableString)

const RawEditableNotes: FC<EditableProps<string>> = ({
  value,
  onChange,
  referenceValues,
}) => {
  const handleTextAreaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value)
    },
    [onChange]
  )

  const handleReferenceSelect = useCallback(
    (val: string | number | undefined) => {
      onChange?.(val as string | undefined)
    },
    [onChange]
  )

  if (!onChange) {
    return <>{formatDescriptionValue(value)}</>
  }

  return (
    <FieldContainer>
      <FullWidthTextArea
        value={value}
        onChange={handleTextAreaChange}
        rows={6}
      />
      {referenceValues && referenceValues.length > 0 && (
        <ReferenceValuesTag
          referenceValues={referenceValues}
          onSelect={handleReferenceSelect}
        />
      )}
    </FieldContainer>
  )
}

export const EditableNotes = memo(RawEditableNotes)

const RawEditableNumber: FC<
  EditableProps<number> & {
    step?: number
    units?: string
    min?: number
    max?: number
    unit?: GeckoMeasurementUnit
    width?: React.CSSProperties['width']
  }
> = ({
  value,
  onChange,
  step,
  units,
  min,
  max,
  referenceValues,
  unit,
  width,
}) => {
  const handleNumberChange = useCallback(
    (newValue: number | null) => {
      onChange?.(newValue ?? undefined)
    },
    [onChange]
  )

  const handleReferenceSelect = useCallback(
    (val: string | number | undefined) => {
      onChange?.(val as number | undefined)
    },
    [onChange]
  )

  if (!onChange) {
    return <>{formatDescriptionValue(value)}</>
  }

  return (
    <FieldContainer>
      <StyledInputNumber
        value={value}
        onChange={handleNumberChange}
        step={step}
        addonAfter={units}
        min={min}
        max={max}
        suffix={unit?.id}
        style={{ width: width }}
      />
      {referenceValues && referenceValues.length > 0 && (
        <ReferenceValuesTag
          referenceValues={referenceValues}
          onSelect={handleReferenceSelect}
        />
      )}
    </FieldContainer>
  )
}

export const EditableNumber = memo(RawEditableNumber)

const RawEditableDate: FC<EditableProps<string>> = ({
  value,
  onChange,
  referenceValues,
}) => {
  const handleDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      onChange?.(date ? date.format(DATE_FORMAT) : undefined)
    },
    [onChange]
  )

  const handleReferenceSelect = useCallback(
    (val: string | number | undefined) => {
      onChange?.(val as string | undefined)
    },
    [onChange]
  )

  if (!onChange) {
    return <>{formatDescriptionValue(value)}</>
  }

  return (
    <FieldContainer>
      <StyledDatePicker
        value={value ? dayjs(value) : null}
        onChange={handleDateChange}
        format={DATE_FORMAT}
      />
      {referenceValues && referenceValues.length > 0 && (
        <ReferenceValuesTag
          referenceValues={referenceValues}
          onSelect={handleReferenceSelect}
        />
      )}
    </FieldContainer>
  )
}

export const EditableDate = memo(RawEditableDate)

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`

const ListItemContent = styled.div`
  flex: 1;
`

const ListItemActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 16px;
`

const AddButtonContainer = styled.div`
  width: 100%;
  margin-top: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const AddButton = styled(Button)`
  width: 100%;
  margin-top: 0.5rem;
`

const FieldContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledReferenceTag = styled(Tag)`
  cursor: pointer;
  margin: 0;
  transition: all 0.2s ease;
  background-color: transparent;

  &:hover {
    background-color: #1890ff;
    color: white;
    border-color: #1890ff;
  }
`

const ReferenceValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
`

const ReferenceValuesTriggerTag = styled(Tag)`
  cursor: pointer;
`

const RawReferenceValueItem: FC<{
  value: string | number | undefined
  onSelect: (value: string | number | undefined) => void
}> = ({ value, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(value)
  }, [value, onSelect])

  return (
    <StyledReferenceTag onClick={handleClick}>
      {formatDescriptionValue(value)}
    </StyledReferenceTag>
  )
}

const ReferenceValueItem = memo(RawReferenceValueItem)

const RawReferenceValuesTag: FC<{
  referenceValues: ReadonlyArray<string | number | undefined>
  onSelect: (value: string | number | undefined) => void
}> = ({ referenceValues, onSelect }) => {
  const content = useMemo(
    () => (
      <ReferenceValuesContainer>
        {referenceValues.map((value, idx) => (
          <ReferenceValueItem key={idx} value={value} onSelect={onSelect} />
        ))}
      </ReferenceValuesContainer>
    ),
    [referenceValues, onSelect]
  )

  return (
    <Popover content={content} trigger='hover' placement='right'>
      <ReferenceValuesTriggerTag>...</ReferenceValuesTriggerTag>
    </Popover>
  )
}

const ReferenceValuesTag = memo(RawReferenceValuesTag)

type EditableListItem = string | number

interface EditableListProps<T extends EditableListItem> {
  items: ReadonlyArray<T>
  setItems: (items: ReadonlyArray<T>) => void
  createNewItem: () => T
  renderItem: (
    value: T,
    onChange: (newValue: T) => void,
    index: number
  ) => ReactNode
}

interface EditableListItemProps<T extends EditableListItem> {
  item: T
  index: number
  renderItem: (
    value: T,
    onChange: (newValue: T) => void,
    index: number
  ) => ReactNode
  onDelete: (index: number) => void
  onChange: (index: number, newValue: T) => void
}

function RawEditableListItemComponent<T extends EditableListItem>({
  item,
  index,
  renderItem,
  onDelete,
  onChange,
}: EditableListItemProps<T>) {
  const handleChange = useCallback(
    (newValue: T) => {
      onChange(index, newValue)
    },
    [index, onChange]
  )

  const handleDelete = useCallback(() => {
    onDelete(index)
  }, [index, onDelete])

  return (
    <ListItem>
      <ListItemContent>{renderItem(item, handleChange, index)}</ListItemContent>
      <ListItemActions>
        <Button
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          type='text'
          danger
          size='small'
        />
      </ListItemActions>
    </ListItem>
  )
}

const EditableListItemComponent = memo(
  RawEditableListItemComponent
) as typeof RawEditableListItemComponent

function RawEditableList<T extends EditableListItem>({
  items,
  setItems,
  createNewItem,
  renderItem,
}: EditableListProps<T>) {
  const handleAdd = useCallback(() => {
    setItems([...items, createNewItem()])
  }, [setItems, items, createNewItem])

  const handleChange = useCallback(
    (index: number, newValue: T) => {
      const newItems = [...items]
      newItems[index] = newValue
      setItems(newItems)
    },
    [setItems, items]
  )

  const handleDelete = useCallback(
    (index: number) => {
      setItems(items.filter((_, i) => i !== index))
    },
    [setItems, items]
  )

  return (
    <div>
      <div>
        {items.map((item, index) => (
          <EditableListItemComponent
            key={index}
            item={item}
            index={index}
            renderItem={renderItem}
            onChange={handleChange}
            onDelete={handleDelete}
          />
        ))}
      </div>
      <AddButtonContainer>
        <Tooltip title='Add Item'>
          <AddButton
            icon={<PlusOutlined />}
            onClick={handleAdd}
            type='dashed'
            size='small'
          />
        </Tooltip>
      </AddButtonContainer>
    </div>
  )
}

const EditableList = memo(RawEditableList) as typeof RawEditableList

function useListItemRenderer<T>(
  renderInnerComponent: (
    key: number,
    value: T,
    onChange: Dispatch<SetStateAction<T | undefined>>,
    referenceValues?: ReadonlyArray<T | undefined>
  ) => ReactNode,
  defaultValue: T,
  referenceValues?: ReadonlyArray<T | undefined>
): (value: T, onChange: (newValue: T) => void, index: number) => ReactNode {
  return useCallback(
    (value: T, onChange: (newValue: T) => void, index: number) => {
      const handleChange: Dispatch<SetStateAction<T | undefined>> = (
        newValue
      ) => {
        const resolved =
          typeof newValue === 'function'
            ? (newValue as (prev: T | undefined) => T | undefined)(value)
            : newValue
        onChange(resolved ?? defaultValue)
      }
      // Pass the same reference values to all items
      return renderInnerComponent(index, value, handleChange, referenceValues)
    },
    [renderInnerComponent, defaultValue, referenceValues]
  )
}

const RawEditableStringList: FC<{
  items: ReadonlyArray<string>
  setItems: (items: ReadonlyArray<string>) => void
  notes?: boolean
  referenceValues?: ReadonlyArray<string | undefined>
}> = ({ items, setItems, notes = false, referenceValues }) => {
  const createNewItem = useCallback(() => '', [])

  const renderInnerComponent = useCallback(
    (
      key: number,
      value: string,
      onChange: Dispatch<SetStateAction<string | undefined>>,
      itemReferenceValues?: ReadonlyArray<string | undefined>
    ) =>
      notes ? (
        <EditableNotes
          key={key}
          value={value}
          onChange={onChange}
          referenceValues={itemReferenceValues}
        />
      ) : (
        <EditableString
          key={key}
          value={value}
          onChange={onChange}
          referenceValues={itemReferenceValues}
        />
      ),
    [notes]
  )

  const renderItem = useListItemRenderer(
    renderInnerComponent,
    '',
    referenceValues
  )

  return (
    <EditableList
      items={items}
      setItems={setItems}
      createNewItem={createNewItem}
      renderItem={renderItem}
    />
  )
}

export const EditableStringList = memo(RawEditableStringList)

const RawEditableNumberList: FC<{
  items: ReadonlyArray<number>
  setItems: (items: ReadonlyArray<number>) => void
  referenceValues?: ReadonlyArray<number | undefined>
}> = ({ items, setItems, referenceValues }) => {
  const createNewItem = useCallback(() => 0, [])

  const renderInnerComponent = useCallback(
    (
      key: number,
      value: number,
      onChange: Dispatch<SetStateAction<number | undefined>>,
      itemReferenceValues?: ReadonlyArray<number | undefined>
    ) => (
      <EditableNumber
        key={key}
        value={value}
        onChange={onChange}
        referenceValues={itemReferenceValues}
      />
    ),
    []
  )

  const renderItem = useListItemRenderer(
    renderInnerComponent,
    0,
    referenceValues
  )

  return (
    <EditableList
      items={items}
      setItems={setItems}
      createNewItem={createNewItem}
      renderItem={renderItem}
    />
  )
}

export const EditableNumberList = memo(RawEditableNumberList)

const RawEditableDateList: FC<{
  items: ReadonlyArray<string>
  setItems: (items: ReadonlyArray<string>) => void
  referenceValues?: ReadonlyArray<string | undefined>
}> = ({ items, setItems, referenceValues }) => {
  const createNewItem = useCallback(() => dayjs().format(DATE_FORMAT), [])

  const renderInnerComponent = useCallback(
    (
      key: number,
      value: string,
      onChange: Dispatch<SetStateAction<string | undefined>>,
      itemReferenceValues?: ReadonlyArray<string | undefined>
    ) => (
      <EditableDate
        key={key}
        value={value}
        onChange={onChange}
        referenceValues={itemReferenceValues}
      />
    ),
    []
  )

  const renderItem = useListItemRenderer(
    renderInnerComponent,
    dayjs().format(DATE_FORMAT),
    referenceValues
  )

  return (
    <EditableList
      items={items}
      setItems={setItems}
      createNewItem={createNewItem}
      renderItem={renderItem}
    />
  )
}

export const EditableDateList = memo(RawEditableDateList)
